// Auth state for GMSomeday. One composable, one fetch on mount.
//
// Usage:
//   import { useAuth } from '@/composables/useAuth'
//   const { user, loading, isAuthenticated, refresh, signIn, signOut } = useAuth()
//
// The user object is the response from /api/auth/me. signIn() opens the
// OAuth flow in the same window; the backend sets a session cookie on
// callback, and subsequent fetches carry it via `credentials: 'include'`.

import { ref, computed } from 'vue'
import { getMe, logout as apiLogout, startGoogleSignIn } from '@/api/client'

const user = ref(null)
const loading = ref(false)
const error = ref('')
let refreshPromise = null

async function _fetchMe() {
  return getMe()
}

export function useAuth() {
  const isAuthenticated = computed(() => user.value?.authenticated === true)

  async function refresh() {
    if (refreshPromise) return refreshPromise
    refreshPromise = (async () => {
      loading.value = true
      error.value = ''
      try {
        user.value = await _fetchMe()
      } catch (e) {
        error.value = String(e)
        user.value = null
      } finally {
        loading.value = false
        refreshPromise = null
      }
    })()
    return refreshPromise
  }

  function signIn(next = '/') {
    startGoogleSignIn(next)
  }

  async function signOut() {
    try {
      await apiLogout()
    } catch {
      // Best-effort — even if the request fails, the user is signing
      // out from this client.
    }
    user.value = null
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    refresh,
    signIn,
    signOut,
  }
}
